import React from "react";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Flame, Droplets, Sparkles, Candy, Snowflake, Quote } from "lucide-react";

const BANNER = "https://images.unsplash.com/photo-1442550528053-c431ecb55509?auto=format&fit=crop&w=2000&q=80";
const PROCESS_IMG = "/tostatura-process.jpg";

const STEPS = [
  { n: "01", icon: Flame, key: "preriscaldamento" },
  { n: "02", icon: Droplets, key: "essiccazione" },
  { n: "03", icon: Sparkles, key: "aromatico" },
  { n: "04", icon: Candy, key: "caramellizzazione" },
  { n: "05", icon: Snowflake, key: "raffreddamento" },
];

const LaTostatura = () => {
  const { t } = useTranslation();
  return (
    <PageShell>
      <SeoHead pageKey="tostatura" image={BANNER} />
      <PageBanner
        eyebrow={t("tostatura.banner.eyebrow")}
        title={t("tostatura.banner.title")}
        subtitle={t("tostatura.banner.subtitle")}
        image={BANNER}
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
            {t("tostatura.introEyebrow")}
          </div>
          <div className="space-y-5 text-[15px] sm:text-lg leading-relaxed" style={{ color: "#3b2417" }}>
            <p dangerouslySetInnerHTML={{ __html: t("tostatura.introP1") }} />
            <p dangerouslySetInnerHTML={{ __html: t("tostatura.introP2") }} />
          </div>
        </div>
      </section>

      <section className="section-y relative overflow-hidden" style={{ background: "#2a160c", color: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img src={PROCESS_IMG} alt={t("tostatura.processAlt")} className="w-full h-[500px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(42,22,12,0.7) 100%)" }} />
              <div className="absolute bottom-5 left-5 right-5">
                <Quote size={22} style={{ color: "#c5371f" }} />
                <p className="font-serif-display text-lg italic mt-2" style={{ color: "#f5ead6" }}>
                  {t("tostatura.quote")}
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>
              {t("tostatura.philoEyebrow")}
            </span>
            <h2 className="font-serif-display font-bold mt-3 leading-tight" style={{ fontSize: "clamp(1.7rem, 2.8vw, 2.4rem)" }}>
              {t("tostatura.philoTitle")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] sm:text-base leading-relaxed" style={{ color: "#ecdcbd" }}>
              <p dangerouslySetInnerHTML={{ __html: t("tostatura.philoP1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("tostatura.philoP2") }} />
              <p dangerouslySetInnerHTML={{ __html: t("tostatura.philoP3") }} />
            </div>
          </div>
        </div>
      </section>

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>
              {t("tostatura.processEyebrow")}
            </span>
            <h2 className="font-serif-display font-bold mt-3" style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3.4vw, 3rem)" }}>
              {t("tostatura.processTitle")}
            </h2>
            <div className="mt-4 flex justify-center">
              <span className="h-1 w-20 rounded-full" style={{ background: "#c5371f" }} />
            </div>
          </div>

          <div className="relative">
            <div
              className="hidden lg:block absolute left-0 right-0 top-[92px] h-0.5"
              style={{ background: "repeating-linear-gradient(to right, #c5371f 0 8px, transparent 8px 16px)" }}
            />
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
              {STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.key}
                    className="relative rounded-2xl p-6 text-center flex flex-col items-center transition hover:-translate-y-1"
                    style={{ background: "#f5ead6", border: "1px solid rgba(91,54,33,0.15)", boxShadow: "0 20px 40px -25px rgba(42,22,12,0.35)" }}
                  >
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 relative z-10" style={{ background: "#c5371f", boxShadow: "0 12px 24px -8px rgba(197,55,31,0.55)" }}>
                      <Icon size={26} color="#fff" />
                    </div>
                    <div className="font-serif-display font-bold text-sm mb-2" style={{ color: "#c5371f", letterSpacing: "0.2em" }}>
                      {t("tostatura.phase")} {s.n}
                    </div>
                    <h3 className="font-serif-display font-bold text-lg" style={{ color: "#2a160c" }}>
                      {t(`tostatura.steps.${s.key}.title`)}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "#5b3621" }}>
                      {t(`tostatura.steps.${s.key}.desc`)}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>

          <p
            className="max-w-3xl mx-auto text-center mt-14 text-base sm:text-lg leading-relaxed"
            style={{ color: "#3b2417" }}
            dangerouslySetInnerHTML={{ __html: t("tostatura.closing") }}
          />
        </div>
      </section>
    </PageShell>
  );
};

export default LaTostatura;
